/** Copy and helpers for Automation Knowledge Pull approval UI (Slice 3F). */

export const AUTOMATION_KNOWLEDGE_PULL_TITLE = 'Automation Knowledge Pull';

export const AUTOMATION_KNOWLEDGE_CAVEAT_TEXT =
  'Automation repositories can contain obsolete, skipped, duplicate, experimental, or rarely executed tests. Stratovai extracts product and coverage signals from the repository, but it cannot yet prove that every script represents current active coverage unless execution history is connected.';

export const AUTOMATION_KNOWLEDGE_ADVANCED_SELECTION_NOTE =
  'Advanced selection by module, path, or uploaded script list is not available in this version.';

export const AUTOMATION_KNOWLEDGE_APPROVE_SUCCESS_MESSAGE =
  'Automation-derived knowledge approved for contextual use. It has not been moved to PKB yet.';

export const AUTOMATION_KNOWLEDGE_APPROVE_BUTTON_LABEL = 'Approve Automation-Derived Knowledge';

export const AUTOMATION_KNOWLEDGE_CAVEAT_CHECKBOX_LABEL =
  'I understand this may include obsolete or inactive automation scripts.';

export function shortSha(value) {
  if (!value || typeof value !== 'string') {
    return '—';
  }
  return value.length > 12 ? `${value.slice(0, 12)}…` : value;
}

export function formatKnowledgeDateTime(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
}

export function pickLatestCompletedGenerationJob(jobs = [], repoConfigId = null) {
  const filtered = (jobs || []).filter((job) => {
    if (job.status !== 'completed') {
      return false;
    }
    if (repoConfigId && job.repo_config_id !== repoConfigId) {
      return false;
    }
    return true;
  });
  if (!filtered.length) {
    return null;
  }
  return filtered.sort((a, b) => {
    const aTime = new Date(a.completed_at || a.queued_at || 0).getTime();
    const bTime = new Date(b.completed_at || b.queued_at || 0).getTime();
    return bTime - aTime;
  })[0];
}

export function candidateCountForJob(job) {
  if (!job?.counts) {
    return 0;
  }
  return Number(job.counts.candidates_extracted || 0);
}

export function isApproveEnabled(job) {
  if (!job || job.status !== 'completed') {
    return false;
  }
  if (job.approval_status === 'approved') {
    return false;
  }
  return candidateCountForJob(job) > 0;
}

export const AUTOMATION_KNOWLEDGE_PROJECTION_CAVEAT_TEXT =
  'I understand this projects reviewed automation-derived knowledge as staged contextual evidence only. Execution status is not connected, and the automation repository may contain obsolete, skipped, duplicate, experimental, generated, rarely executed, or non-CI tests. This does not make the evidence visible to AI Chat or Regression Coverage.';

export const AUTOMATION_KNOWLEDGE_PROJECTION_CAVEAT_CHECKBOX_LABEL =
  'I understand this projects reviewed automation-derived knowledge as staged contextual evidence only. Execution status is not connected, and the automation repository may contain obsolete, skipped, duplicate, experimental, generated, rarely executed, or non-CI tests. This does not make the evidence visible to AI Chat or Regression Coverage.';

export const AUTOMATION_KNOWLEDGE_PROJECT_BUTTON_LABEL = 'Project to staged PKB';

export const AUTOMATION_KNOWLEDGE_RETRACT_BUTTON_LABEL = 'Retract staged projection';

export const AUTOMATION_KNOWLEDGE_STAGED_STATUS_LABEL = 'Projected to staged PKB';

export const AUTOMATION_KNOWLEDGE_STAGED_NON_CONSUMER_NOTE =
  'Not visible to AI Chat or Regression Coverage yet';

export const AUTOMATION_KNOWLEDGE_PROJECTION_SUCCESS_MESSAGE =
  'Automation-derived knowledge projected to staged PKB.';

export const AUTOMATION_KNOWLEDGE_RETRACT_SUCCESS_MESSAGE =
  'Staged automation knowledge projection retracted.';

export function isProjectionSectionVisible(job, candidateCount) {
  if (!job || job.status !== 'completed' || job.approval_status !== 'approved') {
    return false;
  }
  return Number(candidateCount) > 0;
}

export function summarizeProjectionStatuses(projections = [], candidateCount = 0) {
  const rows = Array.isArray(projections) ? projections : [];
  const projected = rows.filter((row) => row.projection_status === 'projected').length;
  const retracted = rows.filter((row) => row.projection_status === 'retracted').length;
  const failed = rows.filter((row) => row.projection_status === 'projection_failed').length;
  const accounted = projected + retracted + failed;
  const notProjected = Math.max(0, Number(candidateCount) - accounted);
  return {
    projected,
    retracted,
    failed,
    notProjected,
  };
}

export function shortCandidateId(value) {
  if (!value || typeof value !== 'string') {
    return '—';
  }
  return value.length > 8 ? `${value.slice(0, 8)}…` : value;
}
